import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/shared/widgets/toast';
import type { PaymentService } from '../../application/services/PaymentService';
import type { PayOptions } from '../../domain/models/payment.types';
import { getExpiryParts } from '../utils/cardPaymentFormValidation';
import {
  createPaymentContextSession,
  savePaymentContextSession,
} from '../utils/paymentContextSession';
import type { PayMode, UpiIntentPollingContext, UpiMode } from './paymentHook.types';
import { useCardPaymentState } from './useCardPaymentState';
import { usePaymentDetails } from './usePaymentDetails';
import { useRazorpayPaymentSetup } from './useRazorpayPaymentSetup';
import { useUpiIntentPolling } from './useUpiIntentPolling';

export type { PayMode, UpiMode } from './paymentHook.types';

export const usePayment = ({
  service,
  paymentContextQueryOverride,
}: {
  service: PaymentService;
  paymentContextQueryOverride?: string;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [paymentMode, setPaymentMode] = useState<PayMode>('upi');
  const [selectedBank, setSelectedBank] = useState<{ code: string; name: string } | null>(null);
  const [upiMode, setUpiMode] = useState<UpiMode>('qr');
  const [showMobileBankDrawer, setShowMobileBankDrawer] = useState(false);
  const [isPaymentSubmitting, setIsPaymentSubmitting] = useState(false);
  const [isUpiIntentPending, setIsUpiIntentPending] = useState(false);
  const [upiIntentPollingContext, setUpiIntentPollingContext] =
    useState<UpiIntentPollingContext | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  const {
    details,
    isDetailsLoading,
    detailsError,
    isExpired,
    paymentSession,
    paymentContextQuery,
    paymentSource,
    razorpayKey,
    razorpayCustomerId,
    expiresAt,
  } = usePaymentDetails({ service, paymentContextQueryOverride });

  const {
    cardName,
    setCardName,
    cardNumber,
    cardExpiry,
    cardCvv,
    setCardCvv,
    saveCard,
    setSaveCard,
    cardNetwork,
    cardNumberError,
    cvvLength,
    handleCardNumberChange,
    handleExpiryChange,
    validateCardForm,
    formatterControls,
    isCardFormValid,
    isExpiryInvalid,
  } = useCardPaymentState();
  const { netBankingBanks, razorpayError } = useRazorpayPaymentSetup({
    service,
    razorpayKey,
    paymentMode,
    formatterControls,
  });

  useUpiIntentPolling({
    amount: details?.amount,
    isUpiIntentPending,
    navigate,
    service,
    upiIntentPollingContext,
    setIsUpiIntentPending,
    setUpiIntentPollingContext,
  });

  useEffect(() => {
    const retryError = (location.state as { paymentError?: unknown } | null)?.paymentError;
    if (typeof retryError === 'string' && retryError.trim()) {
      setPaymentError(retryError);
    }
  }, [location.state]);

  const handleUpiModeChange = useCallback((mode: UpiMode) => {
    setUpiMode(mode);
  }, []);

  const showPaymentError = useCallback((message: string, title = 'Payment Request Failed') => {
    setPaymentError(message);
    toast({
      title,
      description: message,
    });
  }, [toast]);

  const handlePayNow = useCallback(async (
    modeOverride?: PayMode,
    bankOverride?: { code: string; name: string },
    upiModeOverride?: UpiMode
  ) => {
    if (!details || isPaymentSubmitting) return;

    const mode = modeOverride || paymentMode;
    const bank = bankOverride || selectedBank;
    const selectedUpiMode = upiModeOverride || upiMode;

    if (isExpired) {
      showPaymentError('This payment link has expired.', 'Payment Link Expired');
      return;
    }

    if (razorpayError || !razorpayKey) {
      showPaymentError(razorpayError || 'Razorpay key is missing.');
      return;
    }

    if (mode === 'netbanking' && !bank) {
      toast({
        title: 'Required',
        description: 'Please select a bank to proceed.',
      });
      return;
    }

    if (mode === 'card' && !validateCardForm()) {
      toast({
        title: 'Required',
        description: 'Please enter valid credit card details.',
      });
      return;
    }

    const txnId = details.txnId || details.orderId || paymentSession?.txnId;
    if (!txnId) {
      showPaymentError('Transaction details are not available. Please try again.');
      return;
    }

    setIsPaymentSubmitting(true);
    setPaymentError(null);

    try {
      const paymentRequest = await service.createPaymentRequest(txnId);

      const options: PayOptions = {
        amount: paymentRequest.amount,
        currency: paymentRequest.currency,
        orderId: paymentRequest.razorpayOrderId,
        contact: paymentRequest.customerPhone,
        email: paymentRequest.customerEmail,
        key: razorpayKey,
        customerId: razorpayCustomerId,
        callbackUrl: paymentRequest.callbackUrl,
      };

      if (mode === 'card') {
        const [expiryMonth, expiryYear] = getExpiryParts(cardExpiry);
        options.name = cardName;
        options.cardNumber = cardNumber.replace(/\s+/g, '');
        options.cvv = cardCvv;
        options.expiryMonth = expiryMonth;
        options.expiryYear = expiryYear;
        options.savePaymentMethod = saveCard;
      } else if (mode === 'upi') {
        if (selectedUpiMode === 'qr') {
          options.useQr = true;
        } else if (selectedUpiMode === 'bhim') {
          options.upiIntent = true;
        } else {
          options.upiApp = selectedUpiMode;
        }
      } else if (mode === 'netbanking' && bank) {
        options.bankCode = bank.code;
      }

      savePaymentContextSession(
        createPaymentContextSession({
          details: {
            ...details,
            orderId: details.orderId || txnId,
            txnId,
            razorpayKeyId: razorpayKey,
            razorpayCustomerId,
            expiresAt,
          },
          paymentContextQuery,
          retryPath: `${location.pathname}${location.search}`,
        })
      );

      const shouldShowUpiIntentPending = mode === 'upi' && selectedUpiMode !== 'qr';
      if (shouldShowUpiIntentPending) {
        setIsUpiIntentPending(true);
        setUpiIntentPollingContext({ txnId, paymentSource });
      }

      const result = await service.processPayment(options);

      if (!result.success) {
        if (shouldShowUpiIntentPending) {
          setIsUpiIntentPending(false);
          setUpiIntentPollingContext(null);
        }
        showPaymentError(result.error || 'Unable to open payment gateway. Please try again.');
      }
    } catch (err) {
      setIsUpiIntentPending(false);
      setUpiIntentPollingContext(null);
      const errorMsg = err instanceof Error ? err.message : 'Unable to create payment request. Please try again.';
      showPaymentError(errorMsg);
    } finally {
      setIsPaymentSubmitting(false);
    }
  }, [
    cardCvv,
    cardExpiry,
    cardName,
    cardNumber,
    details,
    expiresAt,
    isExpired,
    isPaymentSubmitting,
    location.pathname,
    location.search,
    paymentContextQuery,
    paymentMode,
    paymentSession?.txnId,
    paymentSource,
    razorpayCustomerId,
    razorpayError,
    razorpayKey,
    selectedBank,
    service,
    showPaymentError,
    saveCard,
    toast,
    upiMode,
    validateCardForm,
  ]);

  const handleSelectBank = useCallback((code: string, name: string) => {
    setSelectedBank({ code, name });
  }, []);

  const handleSelectBankMobile = useCallback((code: string, name: string) => {
    setSelectedBank({ code, name });
    setShowMobileBankDrawer(false);
  }, []);

  return {
    paymentMode,
    setPaymentMode,
    selectedBank,
    cardName,
    setCardName,
    cardNumber,
    cardExpiry,
    cardCvv,
    setCardCvv,
    saveCard,
    setSaveCard,
    cardNetwork,
    cardNumberError,
    cvvLength,
    upiMode,
    handleUpiModeChange,
    showMobileBankDrawer,
    setShowMobileBankDrawer,
    netBankingBanks,
    details,
    isLoading: isDetailsLoading,
    detailsError,
    isExpired,
    isPaymentSubmitting,
    isUpiIntentPending,
    paymentError,
    handleCardNumberChange,
    handleExpiryChange,
    handlePayNow,
    handleSelectBank,
    handleSelectBankMobile,
    isCardFormValid,
    isExpiryInvalid,
  };
};
